import { useMemo, useState } from "react";
import { Control, Controller, useController } from "react-hook-form";
import { InstanceVolumeTypeGroup, OvhRegion } from "../../../types/instance";
import { RadioGroup } from "@headlessui/react";
import { DBSCatalog } from "../../../types/database";
import { capitalizeFirstLetter } from "../../../utils";
import { getDBSPriceAmount } from "../../databases/ServiceCreateComponents";
import { getVolumePlanTypeFromPlanCode } from "./utile";
import { humanizeSize } from "../../../utils/file";
import {
  AnimatedFormError,
  Button,
  cn,
  Flex,
  Input,
  Tag,
  Typography,
} from "djuno-design";
import {
  continents,
  ContinentType,
  datacenterDetails,
  getCountryFlag,
  regionsZoneTypes,
  ZoneGroupKey,
} from "../../../utils/countries";
import { Tabs, tabsClasses } from "@mui/material";
import { a11yProps, MuiTabItem, MuiTabPanel } from "../../general/MuiTab";

export const InstanceVolumeRegionInput: React.FC<{
  regions: OvhRegion[];
  control: Control<any>;
  errorMessage: string | undefined;
}> = ({ regions, control, errorMessage }) => {
  const [selectedZoneType, setSelectedZoneType] = useState<ZoneGroupKey>();

  const [tabValue, setTabValue] = useState(0);
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const activeRegions = useMemo(() => {
    return regions
      .filter((r) => r.services.find((s) => s.name === "instance"))
      .filter((r) => r.status === "UP");
  }, [regions]);

  const filteredRegions = useMemo(() => {
    let _filteredRegions = activeRegions;

    if (selectedZoneType) {
      _filteredRegions = activeRegions.filter((r) => {
        if (selectedZoneType === "LZ") {
          return r.type === "localzone";
        } else if (selectedZoneType === "3AZ") {
          return r.type === "region-3-az";
        } else {
          return r.type === "region";
        }
      });
    }

    return _filteredRegions;
  }, [activeRegions, selectedZoneType]);

  const continentsData: Array<
    ContinentType & {
      regions: OvhRegion[];
    }
  > = useMemo(() => {
    const filterRegions = (regions: typeof activeRegions) => {
      let filtered = regions;

      if (selectedZoneType) {
        filtered = filtered.filter((r) => {
          if (selectedZoneType === "LZ") return r.type === "localzone";
          if (selectedZoneType === "3AZ") return r.type === "region-3-az";
          return r.type === "region";
        });
      }

      return filtered;
    };

    const allContinents = continents.map((continent) => {
      const { datacenterLocations } = continent;

      // If datacenterLocations is defined, filter by it; otherwise include all activeRegions
      const regionsInContinent = datacenterLocations
        ? activeRegions.filter((r) =>
            datacenterLocations.includes(r.datacenterLocation)
          )
        : activeRegions;

      const regions = filterRegions(regionsInContinent);

      return {
        ...continent,
        regions,
      };
    });
    return allContinents.filter((c) => c.regions.length > 0);
  }, [activeRegions, selectedZoneType]);

  return (
    <Flex direction="col" className="gap-4">
      {/* regions zone types */}
      <Flex direction="col">
        <Typography.Text className="!text-sm font-semibold">
          Select a deployment mode
        </Typography.Text>
        <RadioGroup
          value={selectedZoneType || null}
          // disabled={disabled}
          onChange={(v) => setSelectedZoneType(v || undefined)}
        >
          <div className="flex flex-col w-full mt-3">
            <div className="grid gap-4 grid-cols-1 md:grid-cols-2 mb-5">
              {regionsZoneTypes.map((zoneType, i) => (
                <RadioGroup.Option key={i} value={zoneType.value}>
                  {({ checked, disabled }) => {
                    return (
                      <div
                        className={cn(
                          "col-span-1 border-2 text-md rounded-xl dark:bg-dark-3 dark:border-gray-400/10 bg-white p-4 shadow hover:shadow-lg transition-all duration-300 cursor-pointer h-full",
                          {
                            "border-primary-400 dark:border-primary-400":
                              checked,
                            "!cursor-not-allowed": disabled,
                          }
                        )}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <Typography.Text className="!text-sm">
                            {zoneType.title}
                          </Typography.Text>
                          <Tag
                            key={i}
                            color={
                              zoneType.value !== "LZ" ? "processing" : undefined
                            }
                          >
                            {zoneType.tag}
                          </Tag>
                        </div>
                        <div className="flex flex-col border-t border-slate-200 dark:border-gray-400/40 mt-2 pt-2 gap-0.5">
                          <Typography.Text
                            uiType="secondary"
                            className="!text-xs"
                          >
                            {zoneType.description}
                          </Typography.Text>
                        </div>
                      </div>
                    );
                  }}
                </RadioGroup.Option>
              ))}
            </div>

            <AnimatedFormError error={errorMessage} />
          </div>
        </RadioGroup>
      </Flex>

      {/* regions */}
      <Flex direction="col">
        <Typography.Text className="!text-sm font-semibold">
          Select a region
        </Typography.Text>
        <Controller
          name="selectedRegion"
          control={control}
          render={({ field: { value, onChange } }) => {
            return (
              <>
                <Tabs
                  value={tabValue}
                  onChange={handleChange}
                  aria-label="basic tabs example"
                  variant="scrollable"
                  sx={{
                    [`& .${tabsClasses.scrollButtons}`]: {
                      "&.Mui-disabled": { opacity: 0.3 },
                    },
                  }}
                >
                  {continentsData.map((continent, index) => (
                    <MuiTabItem
                      label={continent.continentTitle}
                      {...a11yProps(index)}
                    />
                  ))}
                </Tabs>
                <RadioGroup value={value || null} onChange={onChange}>
                  {continentsData.map((continent, index) => (
                    <MuiTabPanel value={tabValue} index={index} key={index}>
                      <div className="flex flex-col w-full mt-3">
                        <div className="grid gap-4 grid-cols-1 md:grid-cols-2 mb-5">
                          {continent.regions.map((region, i) => (
                            <RadioGroup.Option key={i} value={region.name}>
                              {({ checked, disabled }) => {
                                const datacenter = datacenterDetails.find(
                                  (dc) =>
                                    dc.location === region.datacenterLocation
                                );
                                const regionImage = getCountryFlag(
                                  region.countryCode,
                                  "iso2"
                                );
                                return (
                                  <div
                                    className={cn(
                                      "col-span-1 border-2 text-md rounded-xl dark:bg-dark-3 dark:border-gray-400/10 bg-white p-4 shadow hover:shadow-lg transition-all duration-300 cursor-pointer h-full",
                                      {
                                        "border-primary-400 dark:border-primary-400":
                                          checked,
                                        "!cursor-not-allowed": disabled,
                                      }
                                    )}
                                  >
                                    <div className="flex items-center justify-between mb-2">
                                      <Typography.Text className="!text-sm">
                                        {datacenter
                                          ? datacenter.title
                                          : region.name}{" "}
                                        <Typography.Text
                                          uiType="secondary"
                                          className="!text-xs"
                                        >
                                          ({region.name.toUpperCase()})
                                        </Typography.Text>
                                      </Typography.Text>
                                      {regionImage !== "" && (
                                        <img
                                          alt={region.name}
                                          src={regionImage}
                                          className="w-4 h-4"
                                        />
                                      )}
                                    </div>
                                    <div className="flex flex-col border-t border-slate-200 dark:border-gray-400/40 mt-2 pt-2 gap-0.5">
                                      <Tag
                                        key={i}
                                        color={
                                          region.type !== "localzone"
                                            ? "processing"
                                            : undefined
                                        }
                                      >
                                        {region.type === "localzone"
                                          ? "Local Zone"
                                          : region.type === "region-3-az"
                                          ? "3-AZ"
                                          : "1-AZ"}
                                      </Tag>
                                    </div>
                                  </div>
                                );
                              }}
                            </RadioGroup.Option>
                          ))}
                        </div>
                        <AnimatedFormError error={errorMessage} />
                      </div>
                    </MuiTabPanel>
                  ))}
                </RadioGroup>
              </>
            );
          }}
        />
      </Flex>
    </Flex>
  );
};

export const InstanceVolumeTypeInput: React.FC<{
  types: InstanceVolumeTypeGroup[];
  catalogs: DBSCatalog[];
  control: Control<any>;
  errorMessage: string | undefined;
}> = ({ types, catalogs, control, errorMessage }) => {
  return (
    <Controller
      name="selectedType"
      control={control}
      render={({ field: { value, onChange } }) => {
        return (
          <RadioGroup value={value || null} onChange={onChange}>
            <div className="flex flex-col w-full mt-3">
              <div className="grid gap-4 grid-cols-1 md:grid-cols-2 mb-5">
                {types.map((type, i) => {
                  const addon = catalogs.find(
                    (a) =>
                      a.planCode ===
                      `volume.${getVolumePlanTypeFromPlanCode(
                        type.plans[0].code
                      )}.consumption`
                  );

                  const volumData = addon
                    ? addon.blobs?.technical?.volume
                    : null;

                  const exVAT = addon
                    ? getDBSPriceAmount(addon.pricings[0].price, 6)
                    : null;
                  return (
                    <RadioGroup.Option
                      key={type.title + i}
                      value={type.plans[0].code}
                    >
                      {({ checked, disabled }) => {
                        return (
                          <div
                            className={cn(
                              "col-span-1 border-2 text-md rounded-xl dark:bg-dark-3 dark:border-gray-400/10 bg-white p-4 shadow hover:shadow-lg transition-all duration-300 cursor-pointer h-full",
                              {
                                "border-primary-400 dark:border-primary-400":
                                  checked,
                                "!cursor-not-allowed": disabled,
                              }
                            )}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <Typography.Text className="!text-sm">
                                {capitalizeFirstLetter(type.title)}
                              </Typography.Text>
                            </div>
                            <div className="flex flex-col border-t mt-2 pt-2 gap-0.5">
                              {volumData && (
                                <div className="flex items-center ">
                                  {volumData?.iops.guaranteed && (
                                    <Typography.Text className="!text-xs font-medium">
                                      {volumData.iops.level} guaranteed IOPS
                                    </Typography.Text>
                                  )}
                                  {!volumData.iops.guaranteed && (
                                    <Typography.Text className="!text-xs font-medium">
                                      {`Up to ${
                                        volumData.iops.max ||
                                        volumData.iops.level
                                      } IOPS`}
                                    </Typography.Text>
                                  )}
                                  {volumData.capacity.max && (
                                    <Typography.Text className="!text-xs font-medium">
                                      ,{" "}
                                      {humanizeSize(
                                        volumData.capacity.max * 1000000000,
                                        { binaryBaseValue: 1000 }
                                      ).join("B")}{" "}
                                      max
                                    </Typography.Text>
                                  )}
                                </div>
                              )}

                              {exVAT && (
                                <div key={i} className="flex items-center ">
                                  <Typography.Text className="!text-xs font-medium">
                                    ${exVAT}
                                    {/* ex. VAT */}
                                  </Typography.Text>
                                  <Typography.Text className="!text-xs font-medium">
                                    / hour
                                  </Typography.Text>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      }}
                    </RadioGroup.Option>
                  );
                })}
              </div>
              <AnimatedFormError error={errorMessage} />
            </div>
          </RadioGroup>
        );
      }}
    />
  );
};

export const InstanceVolumeCapacityInput: React.FC<{
  control: Control<any>;
  selectedCatalog?: DBSCatalog;
  errorMessage?: string;
  handleGoNexStep?: () => void;
  estimatedPrice: number;
}> = ({
  selectedCatalog,
  control,
  errorMessage,
  handleGoNexStep,
  estimatedPrice,
}) => {
  const technicalData = selectedCatalog
    ? selectedCatalog.blobs?.technical
    : null;

  const {
    field: { value: capacity, onChange: onChangeCapacity },
  } = useController({ name: "capacity", control });

  //calculate maximum capacity value
  const minCapacityValue = 10;

  //calculate maximum capacity value
  const maxCapacityValue = useMemo(() => {
    if (
      technicalData &&
      technicalData.volume &&
      technicalData.volume.capacity
    ) {
      return technicalData.volume.capacity.max;
    } else {
      return minCapacityValue;
    }
  }, [technicalData]);

  //calculate bandwidth value
  const maxBandwidthValue = useMemo(() => {
    if (technicalData && technicalData.bandwidth) {
      return technicalData.bandwidth.max * 1000;
    } else {
      return null;
    }
  }, [technicalData]);

  //calculate bandwidth value
  const bandwidthValue = useMemo(() => {
    if (
      technicalData &&
      technicalData.bandwidth &&
      technicalData.bandwidth.level
    ) {
      const _value = technicalData.bandwidth.level * capacity;
      if (maxBandwidthValue && _value > maxBandwidthValue) {
        return maxBandwidthValue;
      }
      return _value;
    } else {
      return null;
    }
  }, [technicalData, capacity, maxBandwidthValue]);

  //calculate maximum iops value
  const maxIopsValue = useMemo(() => {
    if (technicalData && technicalData.volume) {
      return technicalData.volume.iops.max;
    } else {
      return null;
    }
  }, [technicalData]);

  //calculate iops value
  const iopsValue = useMemo(() => {
    if (
      technicalData &&
      technicalData.volume &&
      technicalData.volume.iops.max
    ) {
      const _value = technicalData.volume.iops.level * capacity;
      if (maxIopsValue && _value > maxIopsValue) {
        return maxIopsValue;
      } else {
        return _value;
      }
    }
    return null;
  }, [technicalData, capacity, maxIopsValue]);

  //handle value changing
  const handleChangeCapacityValue = (value: number) => {
    const _value = value;
    // if (_value < minCapacityValue) {
    //   _value = minCapacityValue;
    // }

    // if (_value > maxCapacityValue) {
    //   _value = maxCapacityValue;
    // }

    onChangeCapacity(_value);
  };

  return (
    <>
      {technicalData &&
        technicalData.volume &&
        technicalData.volume.capacity.max > 1 && (
          <Flex direction="col" className="gap-1 mb-9">
            <div className="w-full md:w-1/2">
              <Input
                type="number"
                min={minCapacityValue}
                max={maxCapacityValue}
                onChange={(e: any) =>
                  handleChangeCapacityValue(Number(e.target.value))
                }
                value={capacity}
                label="Value"
                hint={`max: ${maxCapacityValue}`}
                error={errorMessage}
              />
            </div>

            <Flex direction="col" className="mt-2 gap-1">
              {bandwidthValue !== null && (
                <Typography.Text className="!text-sm">{`Bandwidth: ${bandwidthValue} MB/s`}</Typography.Text>
              )}
              {iopsValue !== null && (
                <Typography.Text className="!text-sm">{`IOPS: ${iopsValue} IOPS`}</Typography.Text>
              )}
              <Typography.Text className="!text-sm">{`Estimated amount: $${getDBSPriceAmount(
                estimatedPrice
              )} /month`}</Typography.Text>
              <Typography.Text className="!text-xs">
                The maximum size depends on your available quota.
              </Typography.Text>
            </Flex>
          </Flex>
        )}

      {handleGoNexStep && (
        <Button
          id="3th-next-button"
          disabled={capacity > maxCapacityValue || capacity < minCapacityValue}
          onClick={handleGoNexStep}
        >
          Next
        </Button>
      )}
    </>
  );
};

export const InstanceVolumeNameInput: React.FC<{
  control: Control<any>;
  errorMessage?: string;
  handleGoNexStep?: () => void;
}> = ({ control, errorMessage, handleGoNexStep }) => {
  const {
    field: { value, onChange },
  } = useController({ name: "volumeName", control });

  return (
    <>
      <div className="w-full md:w-1/2 mb-7">
        <Input
          label="Volume name"
          value={value}
          onChange={onChange}
          error={errorMessage}
          required
        />
      </div>

      {handleGoNexStep && (
        <Button
          id="4th-next-button"
          disabled={
            errorMessage !== undefined || value === undefined || value === ""
          }
          onClick={handleGoNexStep}
        >
          Next
        </Button>
      )}
    </>
  );
};
